import { IGatsbyImageData } from 'gatsby-plugin-image'
import { addDomainOnUrl } from '../../Utils/functionnals'
import { FirmTeaserProps } from '../firms'
import { formatNbOffices } from '../firms/format'
import { mediaType } from '../firms/firm/mapper'
import { SubscriptionType } from '../../types/firm/Subscription'

export type ToolType = {
  name: string
  slug: string
  description: string | null
  logo: string | null
  headerImage: string | IGatsbyImageData | null
  media: mediaType | null
}

export function graphqlToolMapper(data: Queries.ToolQuery): ToolType | null {
  const toolSlug = data?.strapiAccountingTool?.permalink
    ? data?.strapiAccountingTool?.permalink.split('/').pop()
    : null
  const medias = data?.strapiAccountingTool?.medias ?? null
  let toolMedia = null

  if (medias && medias.length > 0 && medias[0]?.id) {
    toolMedia =
      medias[0]?.url?.includes('youtube') ||
      medias[0]?.url?.includes('youtu.be')
        ? {
            id: medias[0].id,
            media: medias[0]?.url,
            video: true,
          }
        : {
            id: medias[0].id,
            media:
              medias[0]?.local?.localFile?.childImageSharp?.gatsbyImageData ||
              null,
            video: false,
          }
  }
  if (!data?.strapiAccountingTool?.name || !toolSlug) {
    return null
  }

  return {
    name: data?.strapiAccountingTool?.name,
    slug: toolSlug,
    description:
      data?.strapiAccountingTool?.description?.data?.description ?? null,
    logo:
      addDomainOnUrl(data?.strapiAccountingTool?.logo?.localFile?.url) || null,
    headerImage:
      data?.strapiAccountingTool?.header?.localFile?.childImageSharp
        ?.gatsbyImageData || null,
    media: toolMedia,
  }
}

export type GraphqlToolFirmType = {
  name: string | null
  slug: string | null
  totalEmployees: string | null
  logo: {
    url: string | null
  } | null
  header: {
    localFile: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData | null
      } | null
    } | null
  } | null
  offices:
    | {
        postalCode: string | null
        city: string | null
        offers:
          | {
              id: string | null
            }[]
          | null
      }[]
    | null
  subscription: {
    name: SubscriptionType | null
  } | null
}

export function graphqlToolFirmMapper(
  graphqlToolFirm: GraphqlToolFirmType,
): FirmTeaserProps | null {
  if (!graphqlToolFirm?.name || !graphqlToolFirm?.slug) {
    return null
  }
  const address =
    graphqlToolFirm.offices && graphqlToolFirm.offices.length > 0
      ? graphqlToolFirm.offices[0]
      : null
  const cityCut =
    address?.city && address?.city.length > 18
      ? `${address?.city.slice(0, 17)}...`
      : address?.city
  const nbOfficesToDisplay = formatNbOffices(
    graphqlToolFirm.offices ? graphqlToolFirm.offices.length : 0,
  )
  const logo = graphqlToolFirm.logo?.url
    ? addDomainOnUrl(graphqlToolFirm.logo.url)
    : null
  const nbTotalOffers = graphqlToolFirm.offices
    ? graphqlToolFirm.offices.reduce(
        (acc, office) => acc + (office.offers ? office.offers.length : 0),
        0,
      )
    : 0

  return {
    name: graphqlToolFirm.name,
    city: `${cityCut} ${
      address?.postalCode ? `(${address.postalCode.slice(0, 2)})` : ''
    }`,
    header: graphqlToolFirm.header?.localFile?.childImageSharp?.gatsbyImageData
      ? graphqlToolFirm.header.localFile.childImageSharp.gatsbyImageData
      : null,
    logo,
    totalEmployees:
      graphqlToolFirm.totalEmployees !== null
        ? graphqlToolFirm.totalEmployees
        : '',
    url: `/cabinets/${graphqlToolFirm.slug}`,
    nbTotalOffers,
    nbOffices: nbOfficesToDisplay,
    subscriptionType: graphqlToolFirm.subscription?.name || undefined,
  }
}

export type ToolTeaserType = {
  id: string
  name: string
  logo: string | null
  url: string
  header: IGatsbyImageData | null
  nbTotalFirms: number
}

export function graphqlToolTeaserMapper(
  tools: Queries.AllPagesQuery['allStrapiAccountingTool']['nodes'],
): ToolTeaserType[] | [] {
  if (!tools || tools.length === 0) {
    return []
  }
  return tools.map((tool) => ({
    id: tool?.id,
    name: tool.name || '',
    logo: addDomainOnUrl(tool.logo?.url) || null,
    header: tool.header?.localFile?.childImageSharp?.gatsbyImageData || null,
    url: `/outils/${tool.permalink?.split('/').pop()}` || '/outils',
    nbTotalFirms: tool.firms?.length || 0,
  }))
}
